import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AccessDenied from './utils/AccessDenied';
import { AppSplash } from '@/components/ui';

const PrivateRoute: React.FC = () => {
  const { user, isLoading, refreshScopes } = useAuth();
  const location = useLocation();
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    if (user) void refreshScopes();
  }, [location.pathname, refreshScopes]);

  // Clear forbidden state on every navigation so the next page loads normally
  useEffect(() => {
    setForbidden(false);
  }, [location.pathname]);

  // Re-register on every navigation so the closure captures the current pathname.
  // Only set forbidden when the 403 originated from a request on THIS page,
  // preventing stale in-flight responses from a previous page triggering it.
  useEffect(() => {
    const currentPath = location.pathname;
    const handler = (e: Event) => {
      const { pathname } = (e as CustomEvent<{ pathname: string }>).detail;
      if (pathname === currentPath) setForbidden(true);
    };
    window.addEventListener('api-forbidden', handler);
    return () => window.removeEventListener('api-forbidden', handler);
  }, [location.pathname]);

  // AuthContext is still rehydrating from localStorage — show the branded
  // splash rather than a bare "Loading…", so a hard refresh does not flash
  // an unstyled screen between the HTML splash and the app.
  if (isLoading) return <AppSplash />;

  if (!user) return <Navigate to="/login" replace />;

  if (forbidden) return <AccessDenied />;

  return <Outlet />;
};

export default PrivateRoute;